import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { G, Circle } from 'react-native-svg';

type PieItem = {
  label: string;
  value: number;
  color: string;
};

type Props = {
  title?: string;
  data: PieItem[];
  size?: number;
};

// Monday.com color palette
const COLORS = {
  text: {
    primary: '#323338',
    secondary: '#676879',
    tertiary: '#9699A6',
  },
  bg: '#F6F7FB',
  border: '#E6E9EF',
};

function formatNum(n: number) {
  try {
    return n.toLocaleString('he-IL');
  } catch {
    return String(n);
  }
}

export default function PieChart({ title, data, size = 200 }: Props) {
  const { total, normalized } = useMemo(() => {
    const clean = (data || []).map((d) => ({
      ...d,
      value: Number.isFinite(d.value) ? Math.max(0, d.value) : 0,
    }));
    const t = clean.reduce((s, d) => s + d.value, 0);
    return { total: t, normalized: clean };
  }, [data]);

  // Thicker stroke for better visibility
  const strokeWidth = 32;
  const r = (size - strokeWidth - 8) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  const segments = useMemo(() => {
    if (!total) return [];
    let acc = 0;
    return normalized
      .filter((d) => d.value > 0)
      .map((d) => {
        const frac = d.value / total;
        const len = frac * circumference;
        const start = acc;
        acc += len;
        return { ...d, frac, len, start };
      });
  }, [normalized, total, circumference]);

  const centerValue = total ? formatNum(total) : '0';

  return (
    <View style={styles.container}>
      {!!title && <Text style={styles.chartTitle}>{title}</Text>}

      <View style={styles.content}>
        {/* Donut Chart */}
        <View style={[styles.chartWrapper, { width: size, height: size }]}>
          <Svg width={size} height={size}>
            <G rotation={-90} origin={`${cx}, ${cy}`}>
              {/* Background ring */}
              <Circle
                cx={cx}
                cy={cy}
                r={r}
                stroke={COLORS.bg}
                strokeWidth={strokeWidth}
                fill="transparent"
              />

              {/* Segments with small gaps */}
              {segments.map((s, i) => {
                const gap = circumference * 0.008; // Small gap between segments
                const segLen = Math.max(0, s.len - gap);
                const dashArray = `${segLen} ${circumference}`;
                const dashOffset = -s.start;

                return (
                  <Circle
                    key={`${s.label}-${i}`}
                    cx={cx}
                    cy={cy}
                    r={r}
                    stroke={s.color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={dashArray}
                    strokeDashoffset={dashOffset}
                    fill="transparent"
                  />
                );
              })}
            </G>
          </Svg>

          {/* Center label */}
          <View style={[styles.centerLabel, { pointerEvents: 'none' }]}>
            <Text style={styles.centerValue}>{centerValue}</Text>
            <Text style={styles.centerText}>סה״כ</Text>
          </View>
        </View>

        {/* Legend */}
        <View style={styles.legend}>
          {(normalized || []).map((d, idx) => {
            const v = Math.max(0, d.value || 0);
            const pct = total ? Math.round((v / total) * 100) : 0;
            
            return (
              <View key={`${d.label}-${idx}`} style={styles.legendItem}>
                <View style={styles.legendLeft}>
                  <View style={[styles.legendDot, { backgroundColor: d.color }]} />
                  <Text style={styles.legendLabel} numberOfLines={1}>
                    {d.label}
                  </Text>
                </View>

                <View style={styles.legendRight}>
                  <Text style={styles.legendValue}>{formatNum(v)}</Text>
                  <Text style={styles.legendPercent}>{pct}%</Text>
                </View>
              </View>
            );
          })}

          {total === 0 && (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>אין נתונים להצגה</Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: 16,
  },

  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.text.primary,
    textAlign: 'right',
  },

  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
  },

  chartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },

  centerLabel: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },

  centerValue: {
    fontSize: 28,
    fontWeight: '600',
    color: COLORS.text.primary,
    letterSpacing: -0.5,
  },

  centerText: {
    fontSize: 13,
    fontWeight: '400',
    color: COLORS.text.secondary,
    marginTop: 2,
  },

  legend: {
    flex: 1,
    gap: 12,
  },

  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: COLORS.bg,
    borderWidth: 1,
    borderColor: COLORS.border,
  },

  legendLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },

  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },

  legendLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: COLORS.text.primary,
    textAlign: 'right',
    flexShrink: 1,
  },

  legendRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  legendValue: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.text.primary,
  },

  legendPercent: {
    fontSize: 13,
    fontWeight: '400',
    color: COLORS.text.secondary,
  },

  emptyState: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: COLORS.bg,
    borderWidth: 1,
    borderColor: COLORS.border,
    alignItems: 'center',
  },

  emptyText: {
    fontSize: 13,
    fontWeight: '400',
    color: COLORS.text.tertiary,
  },
});